import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { ACCESS_DENIED_NOTICE_KEY } from '../constants/access-denied.constants';
import { ROUTE_PATHS } from '../constants/route.constants';
import { buildAllowedOrigins, isAllowedOrigin } from '../utils/auth-origins';
import { SKIP_AUTH, SKIP_AUTH_ERROR } from './auth-context';

@Injectable()
export class AuthErrorInterceptor implements HttpInterceptor {
  private lastHandledAt = 0;
  private readonly cooldownMs = 5000;
  private readonly allowedOrigins = buildAllowedOrigins();
  private readonly sessionCriticalMatchers: ReadonlyArray<RegExp> = [
    /\/users\/current\b/i,
    /\/user\/me\b/i,
  ];

  constructor(private readonly authService: AuthService, private readonly router: Router) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.context.get(SKIP_AUTH) || req.context.get(SKIP_AUTH_ERROR)) {
      return next.handle(req);
    }
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (!isAllowedOrigin(req.url, this.allowedOrigins)) {
            return throwError(() => error);
          }
          if (this.authService.isInteractionInProgress()) {
            return throwError(() => error);
          }
          if (!this.isSessionCriticalRequest(req.url, error)) {
            return throwError(() => error);
          }
          const now = Date.now();
          if (now - this.lastHandledAt > this.cooldownMs) {
            this.lastHandledAt = now;
            if (this.authService.isLoggedIn()) {
              this.authService.logout();
            } else {
              this.router.navigate([ROUTE_PATHS.HOME]).catch(() => {
                // ignore navigation errors
              });
            }
          }
        }
        if (error.status === 403) {
          if (!isAllowedOrigin(req.url, this.allowedOrigins)) {
            return throwError(() => error);
          }
          if (this.authService.isInteractionInProgress()) {
            return throwError(() => error);
          }
          if (this.isUserInactiveError(error)) {
            return throwError(() => error);
          }
          if (!this.isSessionCriticalRequest(req.url, error)) {
            return throwError(() => error);
          }
          const now = Date.now();
          if (now - this.lastHandledAt > this.cooldownMs) {
            this.lastHandledAt = now;
            this.storeAccessDeniedNotice();
            if (this.authService.isLoggedIn()) {
              this.authService.logout();
            } else {
              this.router.navigate([ROUTE_PATHS.HOME]).catch(() => {
                // ignore navigation errors
              });
            }
          }
        }

        return throwError(() => error);
      })
    );
  }

  private isSessionCriticalRequest(url: string, error: HttpErrorResponse): boolean {
    const hasInvalidToken = this.hasInvalidTokenHeader(error);
    if (hasInvalidToken) return true;

    const pathname = this.safePathname(url);
    if (!pathname) return false;
    return this.sessionCriticalMatchers.some((matcher) => matcher.test(pathname));
  }

  private safePathname(url: string): string | null {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return null;
    }
  }

  private hasInvalidTokenHeader(error: HttpErrorResponse): boolean {
    const header = error.headers?.get('WWW-Authenticate') ?? '';
    if (!header) return false;
    const value = header.toLowerCase();
    return value.includes('invalid_token') || value.includes('token_expired');
  }

  private isUserInactiveError(error: HttpErrorResponse): boolean {
    const payload = error.error as { error_type?: string } | null | undefined;
    if (!payload || typeof payload !== 'object') return false;
    return payload.error_type === 'user-inactive';
  }

  private storeAccessDeniedNotice(): void {
    try {
      localStorage.setItem(ACCESS_DENIED_NOTICE_KEY, String(Date.now()));
    } catch {
    }
  }
}
